/* Courted · Perfect days (Ben, 1 Nov: "a way to save an ideal day, or evening … in different places … then
   proactively suggest we actually do them … Friday night through Sunday"). Ours › Perfect days is the list; each one
   is a page by day and part of the day (morning, afternoon, evening, night) with whatever you'd do in it — words you
   type, a place (your yeses in that place first: Toronto's in Toronto, home's at home), and at home a recipe or
   something to watch. Both of you add to it; the other hears in the bell. Plan it puts it on the week for a day you're
   both free (Home's free time), with a time for each thing. Nothing is booked. Pure parts: perfect.js. */
import React from 'react';
import * as H from './helpers.js';
import * as P from './perfect.js';
import * as S from './sync.js';
import { Ico, Art, Plate, Page, Sheet, Google, track } from './ui.jsx';

const { useState, useEffect, useMemo, useRef } = React;
const TIMES = []; for (let m = 6 * 60; m <= 23 * 60 + 45; m += 15) TIMES.push(m);
const TONE = { restaurant: 'is-pop', bar: 'is-sky', fun: 'is-butter', culture: 'is-sky', outdoors: 'is-green', out: 'is-sky' };

/* The two of you, as the bell draws you: whoever put something in it. */
function Orbs({ ids, me, partner }) {
  if (!ids || !ids.length) return null;
  return (
    <span className="co-pd-orbs" aria-hidden="true">
      {ids.slice(0, 2).map((id) => <span key={id} className={'co-pd-orb' + (id === me.id ? '' : ' b')}>{((id === me.id ? me.name : partner && partner.id === id ? partner.name : '?') || '?').charAt(0)}</span>)}
    </span>
  );
}
const whereOf = (p, towns, homeName) => { const t = p.town_id && (towns || []).find((x) => x.id === p.town_id); return t ? H.townWords(t) : 'Home'; };

/* ---- the list (Ours › Perfect days) ---- */
export function DaysR({ list, towns, homeName, me, partner, today, cc, free, busy, nameOf, onOpen, onNew, onPlan, title }) {
  const solo = !partner;
  if (!list) return <div className="co-main"><p className="co-wait">Opening your perfect days…</p></div>;
  return (
    <div className="co-main co-pd">
      {title ? <h1 className="co-display co-pd-h1">Perfect days</h1> : null}
      {!list.length ? (
        <div className="co-pd-empty">
          <h2 className="co-display">Save a perfect day</h2>
          <p>An evening in town, a Sunday at home, a weekend away. Put in what you’d do, and we’ll say when you’re {solo ? '' : 'both '}free for it.</p>
          <button type="button" className="dv-btn dv-btn-pop" onClick={onNew}>Start one</button>
        </div>
      ) : (
        <>
          <p className="dv-lede co-pd-lede">We’ll say when you’re {solo ? '' : 'both '}free for one.</p>
          <div className="co-pd-cards">
            {list.map((p) => {
              const fits = P.fitDates(p, p.items, { free, busy, today, n: 1 });
              const line = P.nextLine(p, p.items, fits, { today, cc, solo, weekKnown: !!free, planned: p.planned_on });
              const sum = P.summary(p.items, nameOf);
              return (
                <div key={p.id} className="dv-card co-pd-card" role="button" tabIndex={0} onClick={() => onOpen(p)} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(p); }}>
                  <div className="co-pd-cardtop"><span className="co-pd-ey">{whereOf(p, towns, homeName)} · {P.lengthWords(p, p.items)}</span><Orbs ids={P.whoAdded(p, p.items, me.id)} me={me} partner={partner} /></div>
                  <h2 className="dv-card-title co-pd-title">{p.title}</h2>
                  {sum ? <p className="co-pd-sum">{sum}</p> : null}
                  <div className="co-pd-foot">
                    <span className={fits.length && !(p.planned_on && p.planned_on >= today) ? 'free' : ''}>{line}</span>
                    {p.items.length ? <button type="button" className="dv-pill" onClick={(e) => { e.stopPropagation(); onPlan(p, fits[0] ? fits[0].date : null); }}>Plan it</button> : null}
                  </div>
                </div>
              );
            })}
          </div>
          <button type="button" className="dv-btn dv-btn-soft co-pd-new" onClick={onNew}><Ico n="i-plus" s />New perfect day</button>
        </>
      )}
    </div>
  );
}

/* ---- a new one: where, how long, what to call it ---- */
export function NewDaySheet({ towns, homeName, town0, partner, search, onAddTown, onCreate, onClose }) {
  const [townId, setTownId] = useState(town0 ? town0.id : null);
  const [days, setDays] = useState(1);
  const [title, setTitle] = useState('');
  const [edited, setEdited] = useState(false);
  const [finding, setFinding] = useState(false);
  const [q, setQ] = useState('');
  const [found, setFound] = useState([]);
  const [busy, setBusy] = useState(null);
  const session = useRef(null);
  const town = townId ? (towns || []).find((t) => t.id === townId) || null : null;
  useEffect(() => { if (!edited) setTitle(P.defaultTitle(days, town)); }, [days, townId, edited]);
  useEffect(() => {
    if (!finding) return undefined;
    const term = q.trim();
    if (term.length < 2) { setFound([]); return undefined; }
    if (!session.current) session.current = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : null;
    let off = false;
    const t = setTimeout(() => { search(term, session.current).then((r) => { if (!off) setFound(r || []); }).catch(() => { if (!off) setFound([]); }); }, 250);
    return () => { off = true; clearTimeout(t); };
  }, [q, finding]);
  const pick = async (r) => {
    setBusy(r.id);
    try { const t = await onAddTown(r, session.current); session.current = null; setTownId(t.id); setFinding(false); setQ(''); setFound([]); } catch (e) { /* the app says it couldn't */ }
    setBusy(null);
  };
  const [saving, setSaving] = useState(false);
  const go = async () => { setSaving(true); try { await onCreate({ title: title.trim() || P.defaultTitle(days, town), townId, days }); } catch (e) { setSaving(false); } };
  const row = (key, ic, tone, label, sub, on, onClick) => (
    <button key={key} type="button" className="dv-row co-when-row" role="radio" aria-checked={on ? 'true' : 'false'} onClick={onClick}>
      <span className={'dv-tile' + (tone ? ' ' + tone : '')}><Ico n={ic} s /></span>
      <span className="dv-row-text"><span>{label}</span>{sub ? <small>{sub}</small> : null}</span>
      {on ? <Ico n="i-check" s /> : null}
    </button>
  );
  return (
    <Sheet title="A new perfect day" onClose={onClose}
      foot={<>{partner ? <p className="co-whonote">{partner.name} can add to it too.</p> : null}<button type="button" className="dv-btn dv-btn-pop" disabled={saving || finding} onClick={go}>{saving ? 'Starting…' : 'Start it'}</button></>}>
      <p className="dv-group-header co-ww-h">Where</p>
      <div className="dv-list dv-list-inset" role="radiogroup" aria-label="Where">
        {row('home', 'i-house', 'is-green', 'At home, or near it', homeName || null, !townId, () => setTownId(null))}
        {(towns || []).map((t) => row(t.id, 'i-pin', '', H.townWords(t), null, townId === t.id, () => setTownId(t.id)))}
        {!finding ? (
          <button type="button" className="dv-row co-when-row co-ww-else" onClick={() => setFinding(true)}>
            <span className="dv-tile co-ww-find"><Ico n="i-search" s /></span><span className="dv-row-text"><span>Somewhere else</span></span>
          </button>
        ) : null}
      </div>
      {finding ? (
        <div className="co-ww-search">
          <label className="dv-field dv-field-inset">
            <Ico n="i-search" s />
            <input type="search" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="A town or city" aria-label="A town or city" enterKeyHint="search" />
          </label>
          {found.length ? (
            <div className="dv-list dv-list-inset">
              {found.map((r) => (
                <button key={r.id} type="button" className="dv-row co-when-row" disabled={!!busy} onClick={() => pick(r)}>
                  <span className="dv-tile"><Ico n="i-pin" s /></span>
                  <span className="dv-row-text"><span>{r.name}</span>{r.detail ? <small>{r.detail}</small> : null}</span>
                  {busy === r.id ? <span className="co-ww-busy" aria-label="Adding" /> : null}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
      <p className="dv-group-header co-ww-h">How long</p>
      <div className="dv-seg dv-seg-inset co-pd-len" role="radiogroup" aria-label="How long">
        <button type="button" role="radio" aria-checked={days === 1 ? 'true' : 'false'} onClick={() => setDays(1)}>One day</button>
        <button type="button" role="radio" aria-checked={days === 3 ? 'true' : 'false'} onClick={() => setDays(3)}>A weekend</button>
      </div>
      <p className="dv-note co-note">{days === 3 ? 'Friday evening to Sunday night.' : 'A whole day, or just the evening.'}</p>
      <p className="dv-group-header co-ww-h">Call it</p>
      <label className="dv-field dv-field-inset">
        <input value={title} onChange={(e) => { setTitle(e.target.value); setEdited(true); }} maxLength={80} aria-label="What to call it" enterKeyHint="done" />
      </label>
    </Sheet>
  );
}

/* ---- one perfect day: its days, its parts, what's in them ---- */
export function DayPage({ p, towns, homeName, me, partner, cardsById, catById, nameOf, onBack, onAdd, onItem, onPlan, onMore }) {
  const [day, setDay] = useState(0);
  // an evening (one day, nothing before the evening) keeps the morning and afternoon to one line until you want them
  const [earlier, setEarlier] = useState(false);
  useEffect(() => { if (day >= p.days) setDay(0); }, [p.days]);
  const names = P.dayNames(p.days);
  const items = p.items || [];
  const sub = (x) => {
    if (x.kind === 'place') {
      const c = cardsById[x.ref], cat = catById[x.ref], a = S.aboutNow(x.ref);
      const bits = [c && !c.missing ? H.whatOf(c, cat) : null, c && c.address ? H.areaOf(c.address) : null].filter(Boolean);
      return <>{a && a.rating ? <span className="co-rating"><Ico n="i-star-f" s />{a.rating.toFixed(1)}</span> : null}{bits.join(' · ')}</>;
    }
    if (x.kind === 'recipe' || x.kind === 'cook') return x.part === 'morning' ? 'Breakfast at home' : 'Cook in';
    if (x.kind === 'title') return String(x.ref || '').startsWith('tv:') ? 'An episode at home' : 'A film at home';
    return null;
  };
  const tile = (x) => {
    if (x.kind === 'place') return <span className={'dv-tile co-pd-ptile ' + (TONE[x.place_kind] || '')}><Plate card={cardsById[x.ref]} kind={x.place_kind || 'fun'} load={false} /></span>;
    if (x.kind === 'recipe' || x.kind === 'cook') return <span className="dv-tile is-butter"><Ico n="i-forkknife" s /></span>;
    if (x.kind === 'title') return <span className="dv-tile is-pop"><Ico n="i-tv" s /></span>;
    return <span className="dv-tile co-pd-ttile"><Ico n="i-pencil" s /></span>;
  };
  const dayName = names[day];
  return (
    <Page onBack={onBack} back="Perfect days" label={p.title} className="co-pd-page"
      right={<>
        <button type="button" className="dv-icon-btn dv-glass co-pd-more" aria-label="More" onClick={onMore}><Ico n="i-more" /></button>
        <button type="button" className="dv-pill dv-pill-pop co-pd-planit" disabled={!items.length} onClick={onPlan}>Plan it</button>
      </>}>
      <div className="co-pd-body">
        <h1 className="co-display co-pd-h1">{p.title}</h1>
        <p className="co-pd-meta"><Orbs ids={P.whoAdded(p, items, me.id)} me={me} partner={partner} /><span>{whereOf(p, towns, homeName)} · {P.lengthWords(p, items)}</span></p>
        {p.days === 3 ? (
          <div className="dv-seg co-pd-days" role="radiogroup" aria-label="Day">
            {names.map((n, i) => <button key={n} type="button" role="radio" aria-checked={i === day ? 'true' : 'false'} onClick={() => setDay(i)}>{n}</button>)}
          </div>
        ) : null}
        {p.days !== 3 && P.isEvening(p, items) && !earlier ? (
          <button type="button" className="dv-quiet co-pd-earlier" onClick={() => setEarlier(true)}><Ico n="i-plus" s />Make it a day: add the morning or afternoon</button>
        ) : null}
        {P.partsOn(p.days, day).filter((part) => earlier || p.days === 3 || !P.isEvening(p, items) || part.id === 'evening' || part.id === 'night').map((part) => {
          const list = P.inPart(items, day, part.id);
          return (
            <section key={part.id} className="co-pd-part" aria-label={dayName ? dayName + ' ' + part.label.toLowerCase() : part.label}>
              <div className="co-pd-parthead"><b>{part.label}</b>{list.length ? <button type="button" className="dv-quiet" onClick={() => onAdd(day, part.id)}><Ico n="i-plus" s />Add</button> : null}</div>
              {list.map((x) => (
                <button key={x.id} type="button" className="co-pd-item" onClick={() => onItem(x)}>
                  {tile(x)}
                  <span className="co-pd-it"><b>{nameOf(x)}</b>{sub(x) ? <small>{sub(x)}</small> : null}</span>
                  {partner && x.added_by ? <span className={'co-pd-orb' + (x.added_by === me.id ? '' : ' b')} aria-label={(x.added_by === me.id ? 'You' : partner.name) + ' added this'}>{(x.added_by === me.id ? me.name : partner.name).charAt(0)}</span> : null}
                </button>
              ))}
              {!list.length ? <button type="button" className="co-pd-emptypart" onClick={() => onAdd(day, part.id)}><Ico n="i-plus" s />Add something for {(dayName ? dayName + ' ' : 'the ') + part.label.toLowerCase()}</button> : null}
            </section>
          );
        })}
        {items.some((x) => x.kind === 'place') ? <Google /> : null}
      </div>
    </Page>
  );
}

/* ---- add to a part: write anything, search the place, or one of your yeses there ---- */
export function AddSheet({ p, day, part, town, homeName, rows, me, partner, cardsById, catById, words, nameOf, homePicks, onAdd, onSwipe, onNeed, onClose }) {
  const [q, setQ] = useState('');
  const [res, setRes] = useState(null);
  const [asked, setAsked] = useState('');
  const field = useRef(null);
  const where = town ? town.name : homeName || 'home';
  const dayName = P.dayNames(p.days)[day];
  const title = (dayName ? dayName + ' ' + part : P.partLabel(part));
  const items = p.items || [];
  const here = (k, ref) => items.find((x) => x.kind === k && x.ref === ref && x.day === day && x.part === part);
  const elsewhere = (k, ref) => items.find((x) => x.kind === k && x.ref === ref && !(x.day === day && x.part === part));
  const inWords = (x) => (P.dayNames(p.days)[x.day] ? P.dayNames(p.days)[x.day] + ' ' + x.part : 'The ' + x.part);
  // the yeses made there: a town's own, or near home's
  const there = useMemo(() => H.rowsIn(rows, town ? town.id : null), [rows, town && town.id]);
  const mineYes = useMemo(() => there.filter((r) => r.user_id === me.id && H.yesLike(r.verdict)), [there, me]);
  const theirYes = useMemo(() => (partner ? there.filter((r) => r.user_id === partner.id && H.yesLike(r.verdict) && !mineYes.some((m) => m.place_id === r.place_id)) : []), [there, partner, mineYes]);
  useEffect(() => { const ids = [...mineYes, ...theirYes].map((r) => r.place_id).slice(0, 30); if (ids.length) onNeed(ids, town ? town.id : null); }, [mineYes, theirYes]);
  const run = async () => {
    const t = q.trim();
    if (t.length < 2) return;
    setAsked(t); setRes('busy');
    try { setRes(await S.searchPlaces(t, town && town.id)); track('perfect_day_searched', { away: !!town }); } catch (e) { setRes('error'); }
  };
  const addPlace = (id, kind, card) => {
    const cat = catById[id];
    onAdd({ kind: 'place', ref: id, label: H.planTitle(kind, cat, words, card && card.type), place_kind: kind });
  };
  const addText = () => { const t = q.trim(); if (!t) return; onAdd({ kind: 'text', ref: null, label: t }); setQ(''); setRes(null); setAsked(''); try { field.current && field.current.focus({ preventScroll: true }); } catch (e) {} };
  const placeRow = (id, kindHint, n) => {
    const card = cardsById[id], cat = catById[id];
    const kind = H.kindOf(id, cat, card, kindHint);
    const name = (cat && cat.name) || (card && !card.missing && card.name) || '';
    const isHere = here('place', id), other = elsewhere('place', id);
    const a = S.aboutNow(id);
    return (
      <div key={id} className="dv-row co-pd-addrow">
        <span className={'dv-tile co-pd-ptile ' + (TONE[kind] || '')}><Plate card={card} kind={kind} load={n < 6} /></span>
        <span className="dv-row-text"><b>{name || H.planTitle(kind, cat, words, card && card.type)}</b>
          <small>{isHere ? 'Here already' : other ? 'In ' + inWords(other) : <>{a && a.rating ? <span className="co-rating"><Ico n="i-star-f" s />{a.rating.toFixed(1)}</span> : null}{[card && !card.missing ? H.whatOf(card, cat, words) : null, card && card.address ? H.areaOf(card.address) : null].filter(Boolean).join(' · ')}</>}</small></span>
        {isHere ? null : <button type="button" className="dv-icon-btn co-pd-plus" aria-label={'Add ' + (name || 'it') + (other ? ' here too' : '')} onClick={() => addPlace(id, kind, card)}><Ico n="i-plus" /></button>}
      </div>
    );
  };
  // at home: your recipes and Cook's yeses, and what you'd watch (Curated), as Our perfect evening offered
  const homeRows = (list, ic, tone, sub) => list.map((x) => {
    const k = x.k;
    const isHere = here(k, x.id), other = elsewhere(k, x.id);
    return (
      <div key={k + x.id} className="dv-row co-pd-addrow">
        <span className={'dv-tile ' + tone}><Ico n={ic} s /></span>
        <span className="dv-row-text"><b>{x.label}</b><small>{isHere ? 'Here already' : other ? 'In ' + inWords(other) : sub(x)}</small></span>
        {isHere ? null : <button type="button" className="dv-icon-btn co-pd-plus" aria-label={'Add ' + x.label} onClick={() => onAdd({ kind: k, ref: x.id, label: x.label })}><Ico n="i-plus" /></button>}
      </div>
    );
  });
  const found = Array.isArray(res) ? res : [];
  let n = 0;
  const typed = q.trim();
  const showYeses = !typed && !asked;
  const recipes = !town && homePicks ? [...homePicks.recipes.map((r) => ({ ...r, k: 'recipe' })), ...homePicks.cook.map((r) => ({ ...r, k: 'cook' }))] : [];
  const mealFirst = part === 'morning' ? recipes.filter((r) => r.meal === 'breakfast').concat(recipes.filter((r) => r.meal !== 'breakfast')) : recipes;
  const titles = !town && homePicks && part !== 'morning' ? homePicks.titles : [];
  return (
    <Sheet title={title.charAt(0).toUpperCase() + title.slice(1)} onClose={onClose} cancel="Done">
      <form className="co-search-form" role="search" onSubmit={(e) => { e.preventDefault(); run(); }}>
        <label className="dv-field dv-field-inset">
          <Ico n="i-search" s />
          <input ref={field} type="search" value={q} onChange={(e) => { setQ(e.target.value); if (!e.target.value.trim()) { setRes(null); setAsked(''); } }} enterKeyHint="search"
            placeholder={(town ? 'Search ' + town.name : 'Search nearby') + ', or write anything'} aria-label={(town ? 'Search ' + town.name : 'Search near ' + where) + ', or write anything'} />
        </label>
      </form>
      {typed ? (
        <div className="dv-list dv-list-inset co-pd-typed">
          <button type="button" className="dv-row co-when-row" onClick={addText}>
            <span className="dv-tile co-pd-ttile"><Ico n="i-pencil" s /></span>
            <span className="dv-row-text"><span>Add “{typed}”</span></span>
          </button>
          {typed !== asked ? (
            <button type="button" className="dv-row co-when-row" onClick={run}>
              <span className="dv-tile co-ww-find"><Ico n="i-search" s /></span>
              <span className="dv-row-text"><span>Search {town ? town.name : 'near ' + where} for “{typed}”</span></span>
            </button>
          ) : null}
        </div>
      ) : null}
      {res === 'busy' ? <p className="co-wait">Looking…</p> : null}
      {res === 'error' ? <p className="dv-note co-note">Couldn’t search just now. Try again in a moment.</p> : null}
      {Array.isArray(res) && !found.length ? <p className="dv-note co-note">Nothing for “{asked}” {town ? 'in ' + town.name : 'near ' + where}. Add it as words instead.</p> : null}
      {found.length ? (
        <>
          <p className="dv-group-header co-ww-h">Best rated {town ? 'in ' + town.name : 'near ' + where}</p>
          <div className="dv-list dv-list-inset">{found.map((c) => placeRow(c.id, null, n++))}</div>
        </>
      ) : null}
      {showYeses && mineYes.length ? (
        <>
          <p className="dv-group-header co-ww-h">Your yeses {town ? 'in ' + town.name : 'near home'}</p>
          <div className="dv-list dv-list-inset">{mineYes.map((r) => placeRow(r.place_id, r.kind, n++))}</div>
        </>
      ) : null}
      {showYeses && theirYes.length ? (
        <>
          <p className="dv-group-header co-ww-h">{partner.name}’s yeses {town ? 'in ' + town.name : 'near home'}</p>
          <div className="dv-list dv-list-inset">{theirYes.map((r) => placeRow(r.place_id, r.kind, n++))}</div>
        </>
      ) : null}
      {showYeses && mealFirst.length ? (
        <>
          <p className="dv-group-header co-ww-h">To cook</p>
          <div className="dv-list dv-list-inset">{homeRows(mealFirst.slice(0, 8), 'i-forkknife', 'is-butter', (r) => (r.k === 'cook' ? 'You said yes in Cook' : 'In your recipes'))}</div>
        </>
      ) : null}
      {showYeses && titles.length ? (
        <>
          <p className="dv-group-header co-ww-h">To watch</p>
          <div className="dv-list dv-list-inset">{homeRows(titles.slice(0, 8).map((t) => ({ ...t, k: 'title' })), 'i-tv', 'is-pop', (t) => (t.watching ? 'You’re watching it' : t.tv ? 'A show' : 'A film'))}</div>
        </>
      ) : null}
      {showYeses ? (
        <div className="dv-list dv-list-inset co-pd-swipe">
          <button type="button" className="dv-row co-when-row" onClick={onSwipe}>
            <span className="dv-tile"><Ico n="i-heart" s /></span>
            <span className="dv-row-text"><span>Swipe on {town ? town.name : 'places near ' + where}</span>
              <small>{part === 'morning' ? 'Cafés, walks and things to do' : part === 'afternoon' ? 'Things to do and places to see' : part === 'evening' ? 'Dinners, shows and things to do' : 'Bars and late nights'}</small></span>
          </button>
        </div>
      ) : null}
      <Google />
    </Sheet>
  );
}

/* ---- one thing: open it, move it, take it off ---- */
export function ItemSheet({ p, item, name, onOpenPlace, onMove, onRemove, onClose }) {
  const names = P.dayNames(p.days);
  const [day, setDay] = useState(item.day);
  return (
    <Sheet title={name} onClose={onClose} cancel="Done">
      {item.kind === 'place' ? (
        <div className="dv-list dv-list-inset">
          <button type="button" className="dv-row co-when-row" onClick={onOpenPlace}>
            <span className="dv-tile"><Ico n="i-pin" s /></span><span className="dv-row-text"><span>See the place</span></span><Ico n="i-chevron" s />
          </button>
        </div>
      ) : null}
      <p className="dv-group-header co-ww-h">Move it to</p>
      {p.days === 3 ? (
        <div className="dv-seg dv-seg-inset co-pd-days" role="radiogroup" aria-label="Day">
          {names.map((n, i) => <button key={n} type="button" role="radio" aria-checked={i === day ? 'true' : 'false'} onClick={() => setDay(i)}>{n}</button>)}
        </div>
      ) : null}
      <div className="co-chipwrap" role="radiogroup" aria-label="Part of the day">
        {P.partsOn(p.days, day).map((pt) => {
          const on = item.day === day && item.part === pt.id;
          return <button key={pt.id} type="button" className="dv-chip" role="radio" aria-checked={on ? 'true' : 'false'} aria-pressed={on ? 'true' : 'false'} onClick={() => { if (!on) onMove(day, pt.id); }}>{pt.label}</button>;
        })}
      </div>
      <button type="button" className="dv-btn dv-btn-plain co-pd-remove" onClick={onRemove}><Ico n="i-trash" s />Take it off</button>
    </Sheet>
  );
}

/* ---- More: rename, or let it go ---- */
export function MoreSheet({ p, onRename, onDelete, onClose }) {
  const [t, setT] = useState(p.title);
  return (
    <Sheet title={p.title} onClose={onClose}
      foot={<button type="button" className="dv-btn dv-btn-pop" disabled={!t.trim() || t.trim() === p.title} onClick={() => onRename(t.trim())}>Save</button>}>
      <p className="dv-group-header co-ww-h">Call it</p>
      <label className="dv-field dv-field-inset"><input value={t} onChange={(e) => setT(e.target.value)} maxLength={80} aria-label="What to call it" /></label>
      <button type="button" className="dv-btn dv-btn-plain co-pd-remove" onClick={onDelete}><Ico n="i-trash" s />Delete this perfect day</button>
    </Sheet>
  );
}

/* ---- Plan it: a day you're both free, and a time for each thing ---- */
export function PlanDaySheet({ p, fits, date0, today, cc, partner, kmAway, townName, nameOf, onPlan, onClose }) {
  const items = P.sorted(p.items);
  const list = useMemo(() => {
    const l = [...fits];
    if (date0 && !l.some((f) => f.date === date0)) l.unshift({ date: date0, end: p.days === 3 ? H.addDays(date0, 2) : date0, from: null, all: false, asked: true });
    return l;
  }, [fits, date0]);
  const [i, setI] = useState(() => { const k = date0 ? list.findIndex((f) => f.date === date0) : 0; return k >= 0 ? k : 0; });
  const [other, setOther] = useState('');
  const fit = i === -1 ? (other ? { date: other, end: p.days === 3 ? H.addDays(other, 2) : other, from: null, all: false } : null) : list[i] || null;
  const base = useMemo(() => P.planTimes(p, items, { from: fit && fit.from }), [fit && fit.date, fit && fit.from, items.length]);
  const [times, setTimes] = useState({});
  useEffect(() => setTimes({}), [fit && fit.date]);
  const at = (x) => (times[x.id] != null ? times[x.id] : base[x.id] && base[x.id].start);
  const [busy, setBusy] = useState(false);
  const names = P.dayNames(p.days);
  // a weekend starts on a Friday: another day snaps back to the Friday before it
  const pickOther = (v) => {
    if (!v) { setOther(''); return; }
    if (p.days === 3) { const d = new Date(v + 'T12:00:00'); const back = (d.getDay() + 2) % 7; setOther(H.addDays(v, -back)); } else setOther(v);
  };
  const go = async () => {
    if (!fit) return;
    setBusy(true);
    const t = {};
    items.forEach((x) => { t[x.id] = { ...base[x.id], start: at(x) }; });
    try { await onPlan({ fit, times: t }); } catch (e) { setBusy(false); }
  };
  const label = fit ? P.dateLabel(fit, cc) : null;
  const dayWord = fit ? (p.days === 3 ? label : P.dayWord(fit.date, today, cc)) : null;
  return (
    <Sheet title={'Plan ' + p.title} onClose={onClose}
      foot={<>
        <p className="co-whonote">{partner ? 'It goes on both your weeks, and ' + partner.name + ' hears now.' : 'It goes on your week.'}</p>
        <button type="button" className="dv-btn dv-btn-pop" disabled={busy || !fit} onClick={go}>{busy ? 'Planning…' : fit ? 'Put it on ' + (dayWord === 'today' ? 'today' : dayWord === 'tomorrow' ? 'tomorrow' : dayWord) : 'Choose a day'}</button>
      </>}>
      <p className="dv-group-header co-ww-h">When</p>
      <div className="dv-list dv-list-inset" role="radiogroup" aria-label="When">
        {list.map((f, k) => (
          <button key={f.date} type="button" className="dv-row co-when-row" role="radio" aria-checked={k === i ? 'true' : 'false'} onClick={() => setI(k)}>
            <span className="dv-row-text"><span>{P.dateLabel(f, cc)}</span>{f.asked ? null : <small>{P.fitLine(p, f, !partner)}</small>}</span>
            {k === i ? <Ico n="i-check" s /> : null}
          </button>
        ))}
        <button type="button" className="dv-row co-when-row" role="radio" aria-checked={i === -1 ? 'true' : 'false'} onClick={() => setI(-1)}>
          <span className="dv-row-text"><span>Another day</span>{i === -1 && other ? <small>{P.dateLabel({ date: other, end: p.days === 3 ? H.addDays(other, 2) : other }, cc)}</small> : null}</span>
          {i === -1 ? <input type="date" className="co-pd-date" min={today} value={other} onChange={(e) => pickOther(e.target.value)} aria-label="Another day" /> : null}
        </button>
      </div>
      {!fits.length && !date0 ? <p className="dv-note co-note">{partner ? 'You’re not both free for it in the next eight weeks, as far as we know. Pick another day.' : 'Pick a day.'}</p> : null}
      {kmAway && kmAway > 15 && townName ? <p className="dv-note co-note">{townName} is about {kmAway < 100 ? Math.round(kmAway / 5) * 5 : Math.round(kmAway / 50) * 50} km from home, so leave time to get there.</p> : null}
      <p className="dv-group-header co-ww-h">On your week</p>
      <div className="dv-list dv-list-inset">
        {items.map((x) => (
          <label key={x.id} className="dv-row co-pd-timerow">
            <span className="dv-row-text"><span>{nameOf(x)}</span><small>{(names[x.day] ? names[x.day] + ' ' + x.part : P.partLabel(x.part))}</small></span>
            <select className="co-select" value={at(x)} onChange={(e) => setTimes((t) => ({ ...t, [x.id]: Number(e.target.value) }))} aria-label={'When for ' + nameOf(x)}>
              {(TIMES.includes(at(x)) ? TIMES : [...TIMES, at(x)].sort((a, b) => a - b)).map((m) => <option key={m} value={m}>{H.ampm(m, true)}</option>)}
            </select>
          </label>
        ))}
      </div>
    </Sheet>
  );
}
